// TODO: consider renaming the file, this is not just about operators anymore

import { last } from "es-toolkit";
import { runInAction } from "mobx";

import { insertMatrixIntoMatrix } from "@/components/expression/utils/matrix";
import {
  deleteMatrixSelection,
  preserveSelectionCursorPosition,
} from "@/components/expression/utils/matrixSelection";
import { mergeInlineEditablesInNode } from "@/components/input/utils/mergeInlineEditables";
import {
  matchingNodeAfter,
  matchingNodeBefore,
} from "@/components/suggestions/providers/utils/filterRules";
import {
  checkNodeCondition,
  checkNodeParentCondition,
  checkSelectedNodesCondition,
} from "@/components/suggestions/providers/utils/nodeCondition";
import {
  insertNodeChildren,
  setNodeDecorators,
  setNodeChildren,
} from "@/components/suggestions/utils";
import { isModelNodeInLockedBlock } from "@/components/tiptap/lock/utils";
import { groupSelection } from "@/input/utils/groupUngroup";
import {
  alignPastedWrapperRefIds,
  resolvePastedComputedResults,
} from "@/models/computedNode/alignPastedWrapperRefIds";
import { cursorIsActive } from "@/models/CursorModel";
import { copyNodePropsFromDoc } from "@/models/nodes/copyNodePropsFromDoc";
import { createNodeFrom } from "@/models/nodes/createNodeFrom";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { LabelNodeModel } from "@/models/nodes/LabelNodeModel";
import { MatrixNodeModel } from "@/models/nodes/MatrixNodeModel";
import { OperatorNodeModel } from "@/models/nodes/OperatorNodeModel";
import { OperatorSymbolNodeModel } from "@/models/nodes/OperatorSymbolNodeModel";
import { PlaceholderNodeModel } from "@/models/nodes/PlaceholderNodeModel";
import { getSymbolContainingNode } from "@/models/nodes/utils";
import { DeleteNodeVisitor } from "@/models/nodes/visitors/DeleteNodeVisitor";
import { EditNodeVisitor } from "@/models/nodes/visitors/EditNodeVisitor";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { isPowerOperator } from "@/models/utils/semanticChecks";
import { trackEvent } from "@/utils/trackEvent";

import { insertLabel } from "./labels";

import type { SingleSuggestion, TrackedHintType } from "@/components/suggestions/types";
import type { ActiveCursorModel, CursorModel } from "@/models/CursorModel";
import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel } from "@/models/nodes/types";
import type {
  SubstitutionRule,
  SubstitutionSiblingCondition,
  AutoGroupCondition,
  LabelNodePosition,
  NodeJson,
} from "@corca/data";

export function insertNode(cursor: ActiveCursorModel, newNode: NodeModel): NodeModel {
  const { node, index } = cursor;
  const { parent } = node;

  if (parent?.is(PlaceholderNodeModel)) {
    cursor.doc.placeholderRegistry.replacePlaceholders(parent, [newNode], parent);

    parent.replaceWith(newNode);
    cursor.moveAfterNode(newNode);

    return newNode;
  }

  if (index !== undefined) {
    const nodeAfterCursor = node.nodes[index];
    if (nodeAfterCursor) {
      nodeAfterCursor.insertSiblingBefore(newNode);
    } else {
      node.appendChild(newNode);
    }
  }
  return newNode;
}

function checkSiblingRule(
  node: NodeModel,
  cond: SubstitutionSiblingCondition | undefined,
  index: number,
) {
  const {
    maxCount,
    untilType,
    passThroughType = ["operator:power"],
  } = cond?.match ?? { maxCount: 1 };
  for (const ptt of passThroughType) {
    if (
      ptt === node.type ||
      (node.is(OperatorNodeModel) && ptt === `operator:${node.operatorType}`) ||
      (node.is(OperatorSymbolNodeModel) &&
        node.semanticTypes?.some((t) => ptt === `operatorSymbol:${t}`))
    ) {
      return "passThrough";
    }
  }

  if (untilType) {
    if (node.is(OperatorNodeModel)) {
      if (untilType?.includes(`${node.type}:${node.operatorType}`)) return false;
    }
    if (node.is(OperatorSymbolNodeModel)) {
      if (node.semanticTypes?.some((t) => untilType?.includes(`${node.type}:${t}`))) return false;
    }
    return !untilType.includes(node.type);
  }
  if (maxCount != null) {
    return index < maxCount;
  }
  return true;
}

function findEmptyInline(node: NodeModel): InlineNodeModel | undefined {
  for (const child of node.childrenPlease) {
    if (child.is(InlineNodeModel) && child.nodes.length === 0) {
      return child;
    }

    const found = findEmptyInline(child);
    if (found) return found;
  }

  return undefined;
}

export function applySuggestion(
  cursor: ActiveCursorModel,
  newNode: NodeModel | null,
  substitution?: SubstitutionRule,
  skipCursorMove?: boolean,
): { node: NodeModel | undefined; cursorMoved: boolean } {
  const { index, node } = cursor;
  const { selectedNodes } = cursor.doc.selection;

  if (index === undefined || index === -1) {
    if (newNode) insertNode(cursor, newNode);
    return { node: newNode ?? undefined, cursorMoved: false };
  }

  if (!substitution) {
    if (newNode) insertNode(cursor, newNode);

    if (cursor.node === node && !skipCursorMove) {
      cursor.moveTo(node, index + 1);
    }
    return { node: newNode ?? undefined, cursorMoved: false };
  }

  const {
    nodesBefore: beforeRule,
    nodesAfter: afterRule,
    selectedNodes: selectedRule,
    cursorPlacement,
    applyTo,
    prependAsChildTo,
  } = substitution;

  const startIndex = selectedNodes.length > 0 ? selectedNodes[0].index : index;
  const endIndex = selectedNodes.length > 0 ? (selectedNodes.at(-1)?.index ?? index) : index;

  const nodesBefore: NodeModel[] = [];

  if (beforeRule) {
    let idx = 0;
    for (let i = startIndex - 1; i >= 0; i -= 1) {
      const child = node.nodes[i];
      const checkResult = checkSiblingRule(child, beforeRule, idx);
      if (!checkResult) break;
      nodesBefore.push(child);
      if (checkResult !== "passThrough") idx += 1;
    }
    nodesBefore.reverse();
  }

  const nodesAfter: NodeModel[] = [];

  if (afterRule) {
    let idx = 0;
    for (let i = endIndex; i < node.nodes.length; i += 1) {
      const child = node.nodes[i];
      const checkResult = checkSiblingRule(child, afterRule, idx);
      if (!checkResult) break;
      nodesAfter.push(child);
      if (checkResult !== "passThrough") idx += 1;
    }
  }

  /**
   * targetNode refers to the node the operation is applied to
   * cursorNode is the basis node for cursorMovement
   * So this:
   *
   *   applyTo: "nodeBefore:label:right-top",
   *   cursorPlacement: "after",
   *
   * should move cursor after nodeBefore, not after the label
   */

  let targetNode: NodeModel | null = newNode;
  let cursorNode = targetNode || cursor.nodeBeforeCursor;

  if (!targetNode) {
    switch (applyTo) {
      case "nodeBefore":
        targetNode = matchingNodeBefore(
          cursor.node,
          startIndex,
          substitution.skipNodesBefore,
          selectedNodes,
        );
        if (targetNode) cursorNode = targetNode;
        break;

      case "nodeAfter":
        targetNode = matchingNodeAfter(
          cursor.node,
          endIndex,
          substitution.skipNodesAfter,
          selectedNodes,
        );
        if (targetNode) cursorNode = targetNode;
        break;

      case "nodeBefore:label:right-top":
      case "nodeBefore:label:left-top":
      case "nodeBefore:label:left-bottom":
      case "nodeBefore:label:right-bottom": {
        const position = last(applyTo.split(":")) as LabelNodePosition;
        const matchedNode = matchingNodeBefore(
          cursor.node,
          startIndex,
          substitution.skipNodesBefore,
          selectedNodes,
        );

        if (matchedNode) {
          const symbolNode = getSymbolContainingNode(matchedNode);

          if (symbolNode) {
            targetNode = insertLabel(symbolNode, position)?.parent || null;
          }

          cursorNode = matchedNode;
        }

        break;
      }

      case "selectedNode:label:right-top":
      case "selectedNode:label:left-top":
      case "selectedNode:label:left-bottom":
      case "selectedNode:label:right-bottom": {
        const position = last(applyTo.split(":")) as LabelNodePosition;
        const matchedNode = selectedNodes.length === 1 ? selectedNodes[0] : null;

        if (matchedNode) {
          const symbolNode = getSymbolContainingNode(matchedNode);

          if (symbolNode) {
            targetNode = insertLabel(symbolNode, position)?.parent || null;
          }

          cursorNode = matchedNode;
        }

        break;
      }

      case "parent:label:right-top":
      case "parent:label:left-top":
      case "parent:label:left-bottom":
      case "parent:label:right-bottom": {
        const position = last(applyTo.split(":")) as LabelNodePosition;
        const matchedNode = cursor.node.parent ?? null;
        if (matchedNode?.is(LabelNodeModel)) {
          targetNode = matchedNode.inlineNode || null;
        } else if (matchedNode) {
          const symbolNode = getSymbolContainingNode(matchedNode);
          if (symbolNode) {
            targetNode = insertLabel(symbolNode, position) || null;
          }
        }
        break;
      }

      case "parent":
        targetNode = cursor.node.parent ?? null;
        if (targetNode) cursorNode = targetNode;
        break;

      case "parent:power": {
        const matchedNode = cursor.node.parent ?? null;
        const powerNode = matchedNode?.nodes.find(isPowerOperator);

        if (powerNode) {
          targetNode = powerNode;
        } else {
          const newPowerNode = cursor.doc.create(OperatorNodeModel, "power");
          matchedNode?.prependChild(newPowerNode);
          targetNode = newPowerNode;
        }

        break;
      }

      case "selectedNode":
        targetNode = selectedNodes.length === 1 ? selectedNodes[0] : null;
        if (targetNode) cursorNode = targetNode;
        break;

      default:
        if (applyTo && applyTo.startsWith("child:")) {
          const arr = applyTo.split(":") as ["child", `${number}`];
          const child = parseInt(arr[1], 10);

          targetNode = cursorNode?.nodes[child] || null;
        }
        break;
    }
  }

  let group: GroupNodeModel | undefined;

  if (
    substitution.autoGroup &&
    selectedNodes.length > 0 &&
    checkSelectedNodesCondition(
      substitution.autoGroup.selectedNodes,
      undefined,
      selectedNodes,
      true,
      substitution.autoGroup.minNodesSelected,
    )
  ) {
    group = groupSelection(cursor.doc, "(", ")", substitution.autoGroup.groupProps);

    if (group) {
      cursor.moveAfterNode(group);
    }
  }

  if (selectedNodes.length > 0 && !group) {
    cursor.moveAfterNode(selectedNodes.at(-1));
  }

  if (substitution.autoGroup && !group) {
    group = autoGroup(cursor.doc, substitution.autoGroup);
  }

  if (newNode) {
    if (prependAsChildTo?.nodeBefore) {
      const prependNode = matchingNodeBefore(
        cursor.node,
        startIndex,
        substitution.skipNodesBefore,
        selectedNodes,
      );

      if (prependNode && checkNodeCondition(prependAsChildTo.nodeBefore, prependNode)) {
        prependNode.prependChild(newNode);
      } else {
        insertNode(cursor, newNode);
      }
    } else {
      insertNode(cursor, newNode);
    }
  }

  if (!targetNode) return { node: undefined, cursorMoved: false };

  if (beforeRule && nodesBefore.length > 0) {
    getTargetMoveToNode(targetNode, beforeRule.moveTo)?.appendChildren(nodesBefore);
  }

  if (afterRule && nodesAfter.length > 0) {
    getTargetMoveToNode(targetNode, afterRule.moveTo)?.appendChildren(nodesAfter);
  }

  if (selectedRule && selectedNodes.length > 0) {
    getTargetMoveToNode(targetNode, selectedRule.moveTo)?.appendChildren(selectedNodes);
  }

  insertNodeChildren(cursor.doc, targetNode, substitution.insertChildren);
  setNodeChildren(cursor.doc, targetNode, substitution.setChildren);

  if ((group?.decorators ?? []).length === 0) {
    setNodeDecorators(cursor.doc, targetNode, substitution.insertDecorators);
  }

  let cursorMoved = false;

  switch (cursorPlacement) {
    case "before":
      cursor.moveBeforeNode(cursorNode);
      cursorMoved = true;
      break;

    case "after":
      cursor.moveAfterNode(cursorNode);
      cursorMoved = true;
      break;

    case "empty": {
      const emptyInline = skipCursorMove ? undefined : findEmptyInline(targetNode);

      if (emptyInline) {
        cursor.moveTo(emptyInline, 0);
        cursorMoved = true;
      }

      break;
    }

    case "label:right-top":
    case "label:right-bottom":
    case "label:left-top":
    case "label:left-bottom": {
      const symbolNode = getSymbolContainingNode(cursorNode);
      if (symbolNode) {
        const [, position] = cursorPlacement.split(":");
        insertLabel(symbolNode, position as LabelNodePosition);
        cursorMoved = true;
      }
      break;
    }

    default:
      if ("parameters" in targetNode || "matrixNode" in targetNode || "unitNode" in targetNode) {
        let position: "start" | "end";
        let child: number;

        if (typeof cursorPlacement === "number") {
          child = cursorPlacement;
          position = "end";
        } else {
          const arr = cursorPlacement.split(":") as ["child", `${number}`, "start" | "end"];
          child = parseInt(arr[1], 10);
          position = arr[2];
        }

        let inlineNode: InlineNodeModel | undefined;
        if ("parameters" in targetNode) {
          inlineNode = targetNode.parameters[child];
        }
        if (!inlineNode && "matrixNode" in targetNode) {
          inlineNode = targetNode.matrixNode?.matrixNodes[child];
        }
        if (!inlineNode && "unitNode" in targetNode && child === 0) {
          inlineNode = targetNode.unitNode?.inlineNode;
        }

        if (inlineNode && !skipCursorMove) {
          cursor.moveTo(inlineNode, position === "end" ? inlineNode.nodes.length : 0);
          cursorMoved = true;
        }
      }
      break;
  }

  if (selectedNodes.length > 0) {
    cursor.doc.selection.resetSelection();
  }

  return { node: "parameters" in targetNode ? targetNode : undefined, cursorMoved };
}

function getTargetMoveToNode(node: NodeModel, moveTo?: number | `label:${string}`) {
  if (moveTo == null) return undefined;

  if (typeof moveTo === "number") {
    if ("parameters" in node) {
      return node.parameters[moveTo];
    }
    if (node.is(GroupNodeModel)) {
      return node.matrixNode?.matrixNodes[moveTo];
    }

    return undefined;
  }
  const symbolNode = getSymbolContainingNode(node);

  if (symbolNode) {
    return insertLabel(symbolNode, moveTo.split(":")[1] as LabelNodePosition);
  }

  return undefined;
}

export function deleteNode(node: NodeModel, index?: number): DeleteNodeVisitor {
  const deleteNodeVisitor = new DeleteNodeVisitor(node, index);
  node.accept(deleteNodeVisitor);
  return deleteNodeVisitor;
}

export function editNode(
  nodeToUpdate: NodeModel,
  updatedNode: NodeModel,
): [node: NodeModel, cursor?: { node: InlineNodeModel; index: number } | false] {
  const editNodeVisitor = new EditNodeVisitor(updatedNode);

  nodeToUpdate.accept(editNodeVisitor);
  updatedNode.doc.editor.resetInput(true);

  if (updatedNode.is(FunctionNodeModel)) {
    return [updatedNode, false];
  }

  return [updatedNode];
}

export function duplicateNode(doc: DocumentModel, node: NodeModel): NodeModel {
  const duplicate = createNodeFrom(node, doc, { resetIds: true });
  alignPastedWrapperRefIds([duplicate]);
  return duplicate;
}

export function pasteNodes(
  doc: DocumentModel,
  selectedNodes: NodeJson[],
  options?: { skipRootIdReset?: boolean; ignoreActiveCursor?: boolean },
  newInlineNode?: InlineNodeModel,
) {
  return runInAction(() => {
    const { skipRootIdReset, ignoreActiveCursor } = options ?? {};
    const { cursor } = doc;

    const resolvedNodes = selectedNodes.flatMap((node) =>
      resolvePastedComputedResults(node, doc, undefined),
    );

    const pastedNodes = resolvedNodes.map((selectedNode) => {
      const pastedNode = copyNodePropsFromDoc(doc, selectedNode);
      const pasteNode = createNodeFrom(pastedNode, doc, { resetIds: true, skipRootIdReset });
      if (!pasteNode) return undefined;

      const isMatrixCellsPaste =
        selectedNodes.length === 1 &&
        pasteNode.is(GroupNodeModel) &&
        pasteNode.matrixNode &&
        Object.keys(pasteNode.props).length === 0;

      if (cursorIsActive(cursor) && !ignoreActiveCursor) {
        // Paste matrix into matrix selection
        if (doc.matrixSelection.currentSelection && isMatrixCellsPaste) {
          const { nodes, node } = doc.matrixSelection.currentSelection;
          insertMatrixIntoMatrix(pasteNode.matrixNode, node, nodes[0].index);
          doc.matrixSelection.resetSelection();
          doc.removeNode(pasteNode);
          return;
        }
        // Paste matrix at matrix cursor
        if (cursor?.node?.parent?.is(MatrixNodeModel) && isMatrixCellsPaste) {
          insertMatrixIntoMatrix(pasteNode.matrixNode, cursor.node.parent, cursor.node.index);
          doc.removeNode(pasteNode);
          return;
        }
        // Regular paste
        insertNode(cursor, pasteNode);
        cursor.moveAfterNode(pasteNode);
      } else if (newInlineNode) {
        if (cursor.node !== newInlineNode) cursor.moveTo(newInlineNode, 0);
        if (cursorIsActive(cursor)) {
          insertNode(cursor, pasteNode);
        }
        cursor.moveAfterNode(pasteNode);
      } else {
        doc.textNode.appendChild(pasteNode);
      }

      mergeInlineEditablesInNode(doc, cursor.node);

      return pasteNode;
    });

    const created = pastedNodes.filter(Boolean);

    alignPastedWrapperRefIds(created);

    return created;
  });
}

export function acceptPhantomHint(
  doc: DocumentModel,
  hintNodes: NodeJson[],
  hintType?: TrackedHintType,
  newInlineNode?: InlineNodeModel,
): void {
  if (doc.id && hintType) {
    trackEvent("hints_suggestion_accepted", {
      hint_type: hintType,
      doc_id: doc.id,
    });
  }

  doc.transact(() => {
    doc.batchCreateNodes(() => {
      return pasteNodes(doc, hintNodes, undefined, newInlineNode);
    });
  }, "PhantomHint:accept");

  doc.editor.hints.setPhantomHints([]);

  // Read after the paste: pasteNodes leaves the cursor either in the inline it just created (an
  // empty table cell) or in the edited expression — in both, the wrapper that may become computed.
  const wrapper = doc.cursor.parentWrapperNode;

  // TODO: we have to accept any command now as "apart" / "factor" have priority in some cases
  // and can in fact produce a computed result
  // ideally things like factoring should be ignored, but it could be hard to detect
  // if there's a meaningful result
  const enablesComputed = true; // command === "solve" || command === "evaluate";

  if (enablesComputed && wrapper && doc.computedNodes.canEnableComputed(wrapper)) {
    doc.computedNodes.setComputed(wrapper, true);
  }
}

/**
 * Accept every hint of a fully-empty formula row at once. For each empty data cell we create an
 * inline expression at its position, paste in the computed result, then enable the computed flag so
 * the cell tracks its inputs.
 */
export function acceptTableRowHints(doc: DocumentModel): void {
  const { editor } = doc;
  const { tiptap } = editor;
  if (!tiptap) return;

  const cells = [...editor.hints.tableRowHints].sort((a, b) => a.contentPos - b.contentPos);
  if (cells.length === 0) return;

  if (doc.id) {
    trackEvent("hints_suggestion_accepted", { hint_type: "table-row", doc_id: doc.id });
  }

  const wrappers = doc.transact(() => {
    return doc.batchCreateNodes(() => {
      const wrappers: WrapperNodeModel[] = [];
      for (const cell of cells) {
        const inlineNode = doc.create(InlineNodeModel);
        tiptap.commands.insertInlineExpression(inlineNode, cell.contentPos);
        const hintNodes = cell.node.type === "inline" ? cell.node.children : [cell.node];
        pasteNodes(doc, hintNodes, { ignoreActiveCursor: true }, inlineNode);
        const { parent } = inlineNode;
        if (parent?.is(WrapperNodeModel)) wrappers.push(parent);
      }
      return wrappers;
    });
  }, "TableRowHints:accept");

  editor.hints.setPhantomHints([]);

  for (const wrapper of wrappers) {
    if (doc.computedNodes.canEnableComputed(wrapper)) {
      doc.computedNodes.setComputed(wrapper, true);
    }
  }

  const lastWrapper = wrappers.at(-1);
  if (lastWrapper) {
    doc.cursor.moveTo(lastWrapper.inlineNode, lastWrapper.inlineNode?.nodes.length ?? 0);
  }
}

export function deleteNodes(nodes: NodeModel[]) {
  nodes.reverse().forEach((node) => {
    deleteNode(node);
  });
}

export function deleteSelectedNodes(doc: DocumentModel): void {
  if (doc.selection.allSelectedNodes.some((node) => isModelNodeInLockedBlock(node))) {
    return;
  }

  doc.transact(() => {
    deleteNodes(doc.selection.allSelectedNodes);
    if (doc.matrixSelection.currentSelection) {
      preserveSelectionCursorPosition(doc, () => {
        deleteMatrixSelection(doc);
      });
    } else {
      deleteMatrixSelection(doc);
    }
    doc.selection.resetSelection();
  }, "deleteSelectedNodes");
}

/**
 * Auto group nodes based on the node operator type. You can optionally add targetNode to group, or it will
 * group the node before cursor by default.
 */
export function autoGroup(doc: DocumentModel, autoGroupNodeCondition: AutoGroupCondition) {
  const { cursor, selection } = doc;
  const { nodeBeforeCursor } = cursor;

  const matchNodeBefore = checkNodeCondition(autoGroupNodeCondition.nodeBefore, nodeBeforeCursor);
  const matchSelected = checkSelectedNodesCondition(
    autoGroupNodeCondition.selectedNodes,
    undefined,
    selection.selectedNodes,
    true,
    autoGroupNodeCondition.minNodesSelected,
  );

  if (matchNodeBefore || matchSelected) {
    if (!matchSelected && nodeBeforeCursor) {
      selection.selectNode(nodeBeforeCursor);
    }

    const group = groupSelection(doc, "(", ")", autoGroupNodeCondition.groupProps);

    if (group) {
      if (autoGroupNodeCondition.insertDecorators) {
        setNodeDecorators(doc, group, autoGroupNodeCondition.insertDecorators);
      }

      doc.cursor.moveRight();
    }

    selection.resetSelection();
    return group;
  }
}

export function prioritizeSuggestionRules(suggestion: SingleSuggestion, cursor: CursorModel) {
  let { priority } = suggestion;

  const { nodeBeforeCursor, nodeAfterCursor, node } = cursor;

  if (suggestion.priorityRules) {
    for (const rule of suggestion.priorityRules) {
      const { condition } = rule;
      if (
        checkNodeCondition(condition.nodeBefore, nodeBeforeCursor) &&
        checkNodeCondition(condition.nodeAfter, nodeAfterCursor) &&
        checkNodeParentCondition(condition.parent, node)
      ) {
        priority += rule.priority ?? 1;
      }
    }
  }
  return priority;
}
